import React, { useEffect, useState } from "react";
import { useI18n, LOCALES } from "../../i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { Menu, Globe, ChevronDown } from "lucide-react";

const navItems = (t) => [
  { key: "why", label: t("nav.why"), href: "#why" },
  { key: "features", label: t("nav.features"), href: "#features" },
  { key: "pricing", label: t("nav.pricing"), href: "#pricing" },
  { key: "how", label: t("nav.how"), href: "#how" },
  { key: "markets", label: t("nav.markets"), href: "#markets" },
];

export default function Header() {
  const { t, locale, setLocale } = useI18n();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const items = navItems(t);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-header border-b border-brand-line" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        <a href="#top" className="flex items-center" data-testid="brand-logo">
          <img
            src={`${process.env.PUBLIC_URL}/green-logo.png`}
            alt="Vinculum"
            className="h-8 md:h-9 w-auto"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {items.map((item) => (
            <a
              key={item.key}
              href={item.href}
              data-testid={`nav-${item.key}-link`}
              className="text-sm text-brand-mute hover:text-brand-ink transition-colors duration-200"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="lang-toggle"
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-brand-line bg-white/60 text-xs uppercase tracking-[0.16em] text-brand-mute hover:text-brand-ink transition-colors"
                aria-label="Change language"
              >
                <Globe size={14} strokeWidth={1.8} />
                <span>{locale.toUpperCase()}</span>
                <ChevronDown size={14} strokeWidth={1.8} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[160px]">
              {LOCALES.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  onClick={() => setLocale(l.code)}
                  data-testid={`lang-option-${l.code}`}
                  className={locale === l.code ? "font-medium text-brand-forest" : ""}
                >
                  <span className="mr-2 text-xs uppercase tracking-[0.16em] text-brand-mute">
                    {l.code}
                  </span>
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <a
            href="#lead"
            data-testid="header-cta-button"
            className="hidden md:inline-flex btn-primary !py-2.5 !px-5"
          >
            {t("nav.cta")}
          </a>

          {/* Mobile menu */}
          <Sheet>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-brand-line bg-white/70"
                aria-label="Open menu"
              >
                <Menu size={18} strokeWidth={1.8} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-brand-ivory border-l border-brand-line">
              <div className="flex flex-col gap-6 pt-10">
                {items.map((item) => (
                  <a
                    key={item.key}
                    href={item.href}
                    data-testid={`mobile-nav-${item.key}-link`}
                    className="font-serif text-3xl text-brand-ink"
                  >
                    {item.label}
                  </a>
                ))}
                <div className="pt-4 border-t border-brand-line flex flex-col gap-3">
                  <a href="#lead" className="btn-primary w-full" data-testid="mobile-cta-button">
                    {t("nav.cta")}
                  </a>
                  <div className="flex gap-2 flex-wrap pt-2">
                    {LOCALES.map((l) => (
                      <button
                        key={l.code}
                        onClick={() => setLocale(l.code)}
                        data-testid={`mobile-lang-${l.code}`}
                        className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.16em] border ${
                          locale === l.code
                            ? "bg-brand-forest text-brand-ivory border-brand-forest"
                            : "border-brand-line text-brand-mute"
                        }`}
                      >
                        {l.code}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
